import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, AlertCircle, CheckCircle, RefreshCw, X } from 'lucide-react';
import apiService from '../services/api';

import logger from '../utils/logger';
interface MessageTemplate {
  id: number;
  template_key: string;
  message: string;
  template_type: 'appointment_reminder' | 'birthday_message' | 'appointment_confirmation' | 'custom';
  trigger_config: any;
  include_confirmation_link: boolean;
  is_active: boolean;
}

interface SendMessagePanelProps {
  appointmentId: string;
  patientName?: string;
  patientPhone?: string;
  appointmentDate?: Date;
  appointmentTime?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const SendMessagePanel: React.FC<SendMessagePanelProps> = ({
  appointmentId,
  patientName,
  patientPhone,
  appointmentDate,
  appointmentTime,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [includeLink, setIncludeLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/api/message-templates') as any;
      setTemplates(response.templates || []);
    } catch (error) {
      logger.error('Failed to load templates:', error);
      setErrorMessage(t('messageTemplates.sendPanel.failedLoadTemplates'));
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = selectedTemplateId
    ? templates.find(t => t.id === selectedTemplateId)
    : null;

  const parseMessage = (message: string) => {
    let parsed = message;
    parsed = parsed.replace(/{patientName}/g, patientName || 'Patient');
    parsed = parsed.replace(/{appointmentDate}/g, appointmentDate?.toLocaleDateString('pt-BR') || '');
    parsed = parsed.replace(/{appointmentTime}/g, appointmentTime || '');
    return parsed;
  };

  const previewMessage = selectedTemplate
    ? parseMessage(selectedTemplate.message)
    : customMessage;

  const handleSend = async () => {
    if (!selectedTemplateId && !customMessage.trim()) {
      setStatus('error');
      setErrorMessage(t('messageTemplates.sendPanel.selectTemplateOrMessage'));
      return;
    }

    setSending(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const response = await apiService.post('/api/message-templates/send-to-appointment', {
        appointment_id: appointmentId,
        template_id: selectedTemplateId || undefined,
        message: !selectedTemplateId ? customMessage : undefined,
        include_confirmation_link: includeLink
      }) as any;

      if (response.success) {
        setStatus('success');
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1500);
      } else {
        setStatus('error');
        let errorMsg = response.error || t('messageTemplates.failedSend');

        if (errorMsg.includes('No LID for user')) {
          errorMsg = t('messageTemplates.sendPanel.noLidError');
        }

        setErrorMessage(errorMsg);
      }
    } catch (error: any) {
      setStatus('error');
      let errorMsg = error.message || t('messageTemplates.failedSend');

      if (errorMsg.includes('No LID for user')) {
        errorMsg = t('messageTemplates.sendPanel.noLidError');
      }

      setErrorMessage(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px',
    border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
    background: 'var(--bg)', color: 'var(--ink)', fontSize: 13,
    fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
    }}>
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        width: '100%', maxWidth: 640, margin: '0 16px',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--line)',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Send size={18} style={{ marginRight: 8, color: 'var(--accent)' }} />
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 500, color: 'var(--ink)', margin: 0 }}>
              {t('messageTemplates.sendPanel.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, minHeight: 0, borderRadius: '50%', border: 'none',
              background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Phone Number Status */}
          {!patientPhone && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              background: 'color-mix(in srgb, var(--warning) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
              borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--warning)',
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{t('messageTemplates.sendPanel.noPhone')}</p>
                <p style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>{t('messageTemplates.sendPanel.noPhoneHint')}</p>
              </div>
            </div>
          )}
          {patientPhone && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
              borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--accent)',
            }}>
              <CheckCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{t('messageTemplates.sendPanel.phone')}: {patientPhone}</p>
                <p style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                  {t('messageTemplates.sendPanel.phoneFormat')}<br/>
                  {t('messageTemplates.sendPanel.phoneExamples')}
                </p>
              </div>
            </div>
          )}

          {/* Template Selection */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('messageTemplates.sendPanel.messageTemplate')}
            </label>
            <select
              value={selectedTemplateId || ''}
              onChange={(e) => {
                setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : null);
                setCustomMessage('');
              }}
              style={inputStyle}
            >
              <option value="">{t('messageTemplates.sendPanel.customMessageOption')}</option>
              {templates.filter(t => t.is_active).map(template => (
                <option key={template.id} value={template.id}>
                  {template.template_key}
                </option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
              {t('messageTemplates.sendPanel.selectTemplateHint')}
            </p>
          </div>

          {/* Custom Message */}
          {!selectedTemplateId && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('messageTemplates.sendPanel.customMessageLabel')}
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                placeholder={t('messageTemplates.sendPanel.customMessagePlaceholder')}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <p style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
                Available: {'{patientName}'}, {'{appointmentDate}'}, {'{appointmentTime}'}, {'{clinicName}'}, {'{professionalName}'}
              </p>
            </div>
          )}

          {/* Message Preview */}
          {previewMessage && (
            <div style={{
              background: 'var(--bg-alt)', border: '1px solid var(--line)',
              borderRadius: 'var(--radius)', padding: 16,
            }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('messageTemplates.sendPanel.messagePreview')}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink)', whiteSpace: 'pre-wrap', margin: 0 }}>{previewMessage}</p>
            </div>
          )}

          {/* Confirmation Link Toggle */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: 12,
            background: 'color-mix(in srgb, var(--success) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
            borderRadius: 'var(--radius)', cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              id="includeLink"
              checked={includeLink}
              onChange={(e) => setIncludeLink(e.target.checked)}
              style={{ accentColor: 'var(--success)' }}
            />
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
              {t('messageTemplates.sendPanel.includeConfirmationLink')}
            </span>
          </label>

          {/* Status Messages */}
          {status === 'success' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'color-mix(in srgb, var(--success) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
              borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--success)',
              fontSize: 13,
            }}>
              <CheckCircle size={18} style={{ flexShrink: 0 }} />
              {t('messageTemplates.sendPanel.messageSentSuccess')}
            </div>
          )}

          {status === 'error' && errorMessage && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
              borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--danger)',
              fontSize: 13,
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
              {errorMessage}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <RefreshCw size={18} style={{ color: 'var(--accent)', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
              <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 8 }}>{t('messageTemplates.loadingTemplates')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '12px 20px', borderTop: '1px solid var(--line)', background: 'var(--bg-alt)',
        }}>
          <button onClick={onClose} className="btn-s">
            {t('messageTemplates.cancel')}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || loading || !patientPhone}
            title={!patientPhone ? t('messageTemplates.sendPanel.phoneRequired') : ''}
            className="btn-s primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {sending ? (
              <>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {t('messageTemplates.sending')}
              </>
            ) : (
              <>
                <Send size={14} />
                {t('messageTemplates.sendPanel.sendMessage')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendMessagePanel;
